import { Avatar, Card, Heading, Text } from "@/design-system";
import { t } from "@/context/language";
import {
  GOOGLE_RATING,
  GOOGLE_REVIEWS,
  GOOGLE_REVIEWS_URL,
} from "@/pages/landingPage/constants/googleReviews";
import { motion } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";

const STARS = [1, 2, 3, 4, 5];

/**
 * Depoimentos vindos do perfil da Fidexa no Google. A nota e o total
 * aparecem uma vez, no cabeçalho, com link para o perfil: é lá que o visitante
 * confere. Os cards não repetem estrelas porque o Google não expõe a nota de
 * cada avaliação no HTML, e inventar seria mentir.
 */
export function GoogleReviews() {
  const copy = t.googleReviews;

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col items-start gap-4 text-left">
        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="border-ds-border bg-ds-surface rounded-ds-md hover:border-ds-brand-500 focus-visible:ring-ds-focus inline-flex min-h-11 cursor-pointer flex-wrap items-center gap-x-2 gap-y-1 border px-3 py-2 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
        >
          <span
            className="flex items-center gap-0.5"
            role="img"
            aria-label={`${copy.ratingLabel} ${GOOGLE_RATING.value} de 5`}
          >
            {STARS.map((star) => (
              <Star
                key={star}
                aria-hidden="true"
                className="fill-ds-brand-500 text-ds-brand-500 size-4"
              />
            ))}
          </span>
          <Text
            as="span"
            size="small"
            weight="semibold"
            className="whitespace-nowrap"
          >
            {GOOGLE_RATING.value} {copy.onGoogle}
          </Text>
          <Text
            as="span"
            size="small"
            tone="subtle"
            className="whitespace-nowrap"
          >
            · {GOOGLE_RATING.count} {copy.reviews}
          </Text>
          <ArrowUpRight aria-hidden="true" className="text-ds-ink-500 size-4" />
        </a>
        <Heading level={2} id="avaliacoes-title" className="md:text-ds-h1">
          {copy.title}
        </Heading>
        <Text size="lead" tone="muted" className="max-w-2xl">
          {copy.subtitle}
        </Text>
      </div>

      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {GOOGLE_REVIEWS.map((review, index) => (
          <motion.li
            key={review.author}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.06 }}
            viewport={{ once: true }}
          >
            <Card className="flex h-full flex-col gap-4">
              <div className="flex items-center gap-3">
                <Avatar name={review.author} size="md" />
                <div className="flex min-w-0 flex-col">
                  <Text as="span" weight="semibold" className="truncate">
                    {review.author}
                  </Text>
                  <Text as="span" size="small" tone="subtle">
                    {copy.sourcePrefix} · {review.when}
                  </Text>
                </div>
              </div>
              <Text as="blockquote" tone="muted">
                “{review.text}”
              </Text>
            </Card>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}
